/* ===================== SEEKER OVERVIEW =====================
   Default Dashboard screen for seeker accounts (astrologers get
   AstrologerOverviewTab via TAB_ROLE_COMPONENTS).

   Everything a stat shows is live: bookings ride the page-level
   useDashboard read (GET /bookings), Active Courses comes from
   GET /enrollments and the order count from GET /orders (shared with
   the Orders tab's first page). The consultations list shows the first
   few CONFIRMED bookings with a skeleton while loading and an empty
   state when there are none — no demo rows. */
import { Link } from 'react-router-dom'
import { FiBookOpen, FiCalendar, FiCompass, FiHome, FiShoppingBag } from 'react-icons/fi'
import { GiCrystalBall, GiCutDiamond, GiLinkedRings, GiLotusFlower } from 'react-icons/gi'
import { FaRegHandPaper } from 'react-icons/fa'
import { appUrl } from '@astro/config'
import { useAstrology, useEnrollments, useOrdersCount, usePujaBookingsCount } from '../useDashboard'

const SERVICES = [
  { name: 'Kundli Matching', sub: 'For Marriage', icon: <GiLinkedRings />, link: "/astro-tools", internal: true },
  { name: 'Vastu Consultation', sub: 'Home & Office', icon: <FiHome />, link: appUrl("jyotish"), internal: false },
  { name: 'Gemstone Suggestion', sub: 'Personalized', icon: <GiCutDiamond />, link: appUrl("shop"), internal: false },
  { name: 'Career Guidance', sub: 'Find Your Path', icon: <FiCompass />, link: appUrl("jyotish", "consultations"), internal: false },
  { name: 'Prashna Kundli', sub: 'Get Answers', icon: <GiCrystalBall />, link: "/astro-tools", internal: true },
  { name: 'Palm Reading', sub: 'Life Insights', icon: <FaRegHandPaper />, link: appUrl("jyotish", "book-maharaj-ji"), internal: false },
  { name: 'Spiritual Guidance', sub: 'Inner Peace', icon: <GiLotusFlower />, link: appUrl("jyotish"), internal: false },
]

/* The overview shows only the first N CONFIRMED bookings — pending,
   completed and cancelled rows live on the Consultations tab. */
const TIMELINE_LIMIT = 4

export default function OverviewTab({ onSwitch, data }) {
  const consultations = data?.consultations ?? []
  const consultationsLoading = Boolean(data?.isLoading)
  const upcoming = consultations.filter((c) => c.join)
  const timeline = consultations
    .filter((c) => String(c.status).toLowerCase() === 'confirmed')
    .slice(0, TIMELINE_LIMIT)

  const { activeCount, enrollments, isLoading: coursesLoading } = useEnrollments()
  const { total: orderCount, isLoading: ordersLoading } = useOrdersCount()
  const { total: pujaCount, isLoading: pujasLoading } = usePujaBookingsCount()

  // Live astrology (VedAstro) with graceful fallback to the reference content.
  // Fetched here (not at page level) so only astrology tabs hit the upstream.
  const astro = useAstrology()
  const horo = astro?.hasHoroscope ? astro.horoscope : null
  const moonSign = astro?.moonSign || 'Virgo'
  const moonGlyph = astro?.moonGlyph || '♍'
  const horoExcerpt = horo ? `${horo.text.split('. ')[0]}.` : 'A productive day for planning and taking practical steps.'
  const pan = astro?.panchang
  const panNak = pan ? String(pan.nakshatra).split(' - ')[0] : 'Uttara Ashadha'

  return (
    <div className="space-y-8">
      {/* Live stat cards. (No greeting banner here — the page-level hero above
          already greets the seeker; a second banner read as a duplicate.) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard tone="#3D0A0A" label="My Consultations" icon={<FiCalendar size={20} />}
          value={consultationsLoading ? null : String(consultations.length)}
          caption='View all'
          onClick={() => onSwitch('appoinments')} />
        <StatCard tone="#D48806" label="Active Courses" icon={<FiBookOpen size={20} />}
          value={coursesLoading ? null : String(activeCount)}
          caption='Go to Academy'
          onClick={() => onSwitch('courses')} />
        <StatCard tone="#3D0A0A" label="My Orders" icon={<FiShoppingBag size={20} />}
          value={ordersLoading ? null : String(orderCount)}
          caption="All orders"
          onClick={() => onSwitch('orders')} />
        <StatCard tone="#D48806" label="Puja Bookings" icon={<GiLotusFlower size={20} />}
          value={pujasLoading ? null : String(pujaCount)}
          caption="View bookings"
          onClick={() => onSwitch('pujabookings')} />
      </div>

      {/* Two-column section */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 p-3 bg-stone-50 rounded-2xl items-start">
        {/* Upcoming consultations */}
        <div className="md:col-span-9 bg-white p-5 border border-stone-100 shadow-sm rounded-2xl w-full">
          <div className="flex flex-wrap justify-between items-center gap-x-4 gap-y-2 mb-5">
            <h3 className="text-lg font-bold text-stone-900">Upcoming Consultations</h3>
            <button onClick={() => onSwitch('appoinments')} className="text-xs font-bold text-[#8B4513] uppercase tracking-wider hover:underline">View All</button>
          </div>
          {consultationsLoading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <BookingSkeletonRow key={i} />
              ))}
            </div>
          ) : timeline.length === 0 ? (
            <div className="py-10 text-center">
              <div className="text-4xl mb-3 opacity-40">🗓️</div>
              <p className="text-base font-semibold text-astroDark mb-1">No confirmed consultations</p>
              <p className="text-sm text-stone-400 mb-4">Confirmed sessions appear here — book one, or check all bookings under Appointments.</p>
              <button onClick={() => onSwitch('appoinments')} className="bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl hover:shadow-lg hover:shadow-goldPrimary/25 transition-all">
                Book a Session
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {timeline.map((c, i) => (
                <div key={c.key} className="group border border-amber-900/10 rounded-xl p-3.5 bg-amber-50/50 hover:shadow-md hover:border-[#D4A017]/40 transition-all">
                  <div className="flex gap-3 items-start">
                    <div className={`text-white w-14 h-14 rounded-xl flex flex-col items-center justify-center shrink-0 shadow-lg ${i === 0 ? 'bg-[#3D0A0A] shadow-[#3D0A0A]/20' : 'bg-[#D48806] shadow-amber-600/20'}`}>
                      <span className="text-lg font-bold leading-none">{c.day}</span>
                      <span className="text-[8px] uppercase tracking-widest opacity-80 mt-0.5">{c.mon}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-amber-800/60">Booking {c.code}</p>
                          {/* Lead with WHO the session is with — the meet type
                              is just the Mode fact below, nothing more. */}
                          <p className="text-sm font-bold text-stone-900 leading-tight truncate mt-0.5">{c.person}</p>
                        </div>
                        <span className={`text-[9px] font-bold uppercase tracking-wide px-2 py-1 rounded-full shrink-0 ${c.cls}`}>{c.status}</span>
                      </div>
                    </div>
                  </div>
                  {/* Booking facts pulled straight off the API record. */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-2 mt-3 pt-3 border-t border-amber-900/10">
                    <Fact label="Date" value={c.dateLabel} />
                    <Fact label="Time" value={c.time} />
                    <Fact label="Mode" value={c.mode} />
                    <Fact label="Duration" value={c.dur} />
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-amber-900/10">
                    {c.amount ? (
                      <div>
                        <span className="num text-base font-bold text-goldAccent">{c.amount}</span>
                        <span className="text-[8px] text-stone-400 block uppercase tracking-wider">Session Fee</span>
                      </div>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider text-stone-300">✦</span>
                    )}
                    {c.id != null && (
                      <a
                        href={appUrl('jyotish', `/bookings/${c.id}`)}
                        className="border border-stone-200 px-4 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider text-stone-700 hover:bg-stone-50"
                      >
                        View Details
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="md:col-span-3 space-y-3">
          {/* Horoscope */}
          <div className="bg-white p-3 border border-stone-100 shadow-sm rounded-2xl flex flex-col">
            <div className="flex flex-wrap justify-between items-center gap-x-3 gap-y-1.5 mb-3">
              <h3 className="text-[10px] font-bold text-stone-800 uppercase tracking-wide">Today's Horoscope</h3>
              <span className="text-[8px] font-bold bg-stone-50 text-stone-500 px-1.5 py-0.5 rounded">{moonSign} {moonGlyph}</span>
            </div>
            <div className="flex flex-col items-center text-center mb-3">
              <div className="w-24 h-24 border-2 border-stone-100 rounded-full flex items-center justify-center mb-2 text-4xl text-cosmicRed bg-amber-50/40">{moonGlyph}</div>
              <p className="text-stone-500 text-[9px] leading-tight px-1 italic">&ldquo;{horoExcerpt}&rdquo;</p>
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              <div className="bg-stone-50 p-1 rounded-lg text-center border border-stone-100">
                <p className="text-[7px] text-stone-400 uppercase font-bold">Lucky No.</p>
                <p className="text-[10px] font-black text-amber-900">{horo?.luckyNum ?? '6'}</p>
              </div>
              <div className="bg-stone-50 p-1 rounded-lg text-center border border-stone-100">
                <p className="text-[7px] text-stone-400 uppercase font-bold">Color</p>
                <p className="text-[10px] font-black text-green-700">{horo?.luckyColor ?? 'Green'}</p>
              </div>
              <div className="bg-stone-50 p-1 rounded-lg text-center border border-stone-100">
                <p className="text-[7px] text-stone-400 uppercase font-bold">Mood</p>
                <p className="text-[10px] font-black text-amber-900">{horo?.luckyMood ?? 'Focused'}</p>
              </div>
            </div>
            <button onClick={() => onSwitch('horoscope')} className="w-full bg-amber-600 text-white py-1.5 rounded-lg font-bold text-[8px] uppercase tracking-wider hover:bg-amber-700 transition-all mb-4 mt-6">Read Full Horoscope</button>
          </div>

          {/* Panchang + journey */}
          <div className="space-y-3 h-full">
            <div className="bg-white p-4 border border-stone-100 shadow-sm rounded-2xl w-full">
              <div className="flex justify-between items-start mb-2">
                <h3 className="text-[11px] font-bold text-stone-700">Today's Panchang</h3>
                <span className="text-sm">🗓️</span>
              </div>
              <h2 className="text-sm font-bold text-red-900 mb-1">Tithi: {pan?.tithi?.Name ?? 'Ekadashi'}</h2>
              <p className="text-[9px] text-stone-500 mb-4 leading-tight">Auspicious day for spiritual practices, fasting, and meditation. Good time to start new learning and financial planning.</p>
              <div className="grid grid-cols-2 gap-2 mb-4">
                <div className="bg-stone-50 border border-stone-100 p-2 rounded-lg text-left">
                  <p className="text-[7px] text-stone-500 uppercase font-bold tracking-wider">Nakshatra</p>
                  <p className="text-[11px] text-amber-700 font-black">{panNak}</p>
                </div>
                <div className="bg-stone-50 border border-stone-100 p-2 rounded-lg text-left">
                  <p className="text-[7px] text-stone-500 uppercase font-bold tracking-wider">Direction</p>
                  <p className="text-[11px] text-amber-700 font-black">{pan?.dishaShool ?? 'West'}</p>
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 mb-4">
                <div className="col-span-8 bg-stone-50 border border-stone-100 p-2 rounded-lg text-left">
                  <p className="text-[7px] text-stone-500 uppercase font-bold tracking-wider">Rahukal</p>
                  <p className="text-[10px] text-amber-700 font-black">{pan?.rahuKalam ?? '09:00 - 10:30'}</p>
                </div>
                <div className="col-span-4 bg-stone-50 border border-stone-100 p-2 rounded-lg text-left">
                  <p className="text-[7px] text-stone-500 uppercase font-bold tracking-wider">Yog</p>
                  <p className="text-[11px] text-amber-700 font-black">{pan?.yoga?.Name ?? 'Shukla'}</p>
                </div>
              </div>
              <button className="w-full border border-stone-200 py-1.5 rounded-md text-[8px] font-bold text-stone-700 hover:bg-stone-50 uppercase tracking-widest">View Full Panchang</button>
            </div>
            <div className="bg-white p-4 border border-stone-100 shadow-sm rounded-2xl flex items-center justify-between">
              <div>
                <h3 className="text-[11px] font-bold">Track Your Spiritual Journey</h3>
                <p className="text-[9px] text-stone-400 mt-0.5">Complete your daily activities.</p>
              </div>
              <div className="w-10 h-10 rounded-full border-4 border-amber-600 flex items-center justify-center font-bold text-[9px] shrink-0">72%</div>
            </div>
          </div>
        </div>
      </div>

      {/* Explore services — static marketing content. `internal` cards route
          within this app via react-router Link; the rest are plain anchors to
          their `link` (falling back to the consultation/jyotish app). */}
      <div className="bg-white p-4 sm:p-6 border border-stone-100 shadow-sm rounded-2xl w-full">
        <div className="flex flex-wrap justify-between items-center gap-x-4 gap-y-2 mb-6">
          <h2 className="text-base font-bold text-stone-900">Explore Our Services</h2>
          {/* <a href={appUrl('jyotish')} className="text-xs font-bold text-red-900 hover:underline">View All Services</a> */}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {SERVICES.map((s) => {
            const cardCls = 'flex flex-col items-center text-center p-3 border border-stone-100 rounded-xl hover:shadow-md hover:border-[#D4A017]/40 transition-all'
            const inner = (
              <>
                <div className="p-3 bg-orange-50 rounded-full text-orange-600 mb-2 text-lg leading-none">{s.icon}</div>
                <p className="text-[11px] font-bold text-stone-900 mb-0.5">{s.name}</p>
                <p className="text-[9px] text-stone-500">{s.sub}</p>
              </>
            )
            return s.internal ? (
              <Link key={s.name} to={s.link} className={cardCls}>{inner}</Link>
            ) : (
              <a key={s.name} href={s.link} className={cardCls}>{inner}</a>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/** One booking fact — tiny tracked label over the value, the dashboard's idiom. */
function Fact({ label, value }) {
  return (
    <div>
      <p className="text-[8px] font-bold uppercase tracking-[0.18em] text-stone-400">{label}</p>
      <p className="text-xs font-semibold text-astroDark mt-1">{value ?? '—'}</p>
    </div>
  )
}

/* Pulsing placeholder matching one booking card — date block, headline,
   status pill and the facts grid — shown while the bookings read loads. */
function BookingSkeletonRow() {
  return (
    <div className="border border-amber-900/10 rounded-xl p-3.5 bg-amber-50/50 animate-pulse">
      <div className="flex gap-3 items-start">
        <div className="w-14 h-14 rounded-xl bg-stone-100 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="space-y-2 flex-1 min-w-0">
              <div className="h-2.5 w-24 bg-stone-100 rounded-full" />
              <div className="h-3.5 w-40 bg-stone-100 rounded-full" />
            </div>
            <div className="h-5 w-16 bg-stone-100 rounded-full shrink-0" />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 pt-3 border-t border-amber-900/10">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="space-y-1.5">
            <div className="h-2 w-10 bg-stone-100 rounded-full" />
            <div className="h-3 w-16 bg-stone-100 rounded-full" />
          </div>
        ))}
      </div>
    </div>
  )
}

/* Stat card on the design system's .stat-card surface (rounded, hover lift,
   top accent bar driven by --accent) — same surface as the astrologer
   overview so both roles' dashboards read as one family. Vertical layout:
   icon chip + label row, the live value (pulse placeholder while its query
   loads), then the caption, which is the tap target when a destination tab
   exists. `icon` is a rendered react-icons element. */
function StatCard({ tone, label, value, caption, onClick, icon }) {
  return (
    <div className="stat-card h-full flex flex-col" style={{ '--accent': tone }}>
      <div className="flex items-center gap-3">
        <div className="stat-icon shrink-0 text-white" style={{ background: tone }}>
          {icon}
        </div>
        <p className="text-stone-500 text-[11px] font-bold uppercase tracking-wider leading-snug">{label}</p>
      </div>
      {value == null ? (
        <div className="h-6 w-16 bg-stone-100 rounded-full mt-3.5 mb-1 animate-pulse" />
      ) : (
        <h3 className="text-[22px] font-bold text-stone-900 mt-3 leading-tight break-words">{value}</h3>
      )}
      {onClick ? (
        <button onClick={onClick} className="mt-1 w-max text-left text-xs font-semibold text-[#A67C00] hover:underline cursor-pointer">{caption}</button>
      ) : (
        <span className="mt-1 text-xs font-semibold text-[#A67C00]">{caption}</span>
      )}
    </div>
  )
}
